import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Label,
  toast,
  Typography,
} from "@altimateai/lego";
import { LoadingButton, Stack } from "@altimateai/lego";
import { useMutation } from "@tanstack/react-query";
import { addDatastore } from "@/helpers/apis";
import { ConnectionProps } from "./types";
import { useForm } from "react-hook-form";
import { TableauDatastoreRequest } from "../../../types";
import { useEffect } from "react";
import { TableauConnectionHeader } from "./TableauConnection/TableauConnectionHeader";
import { useTableauConnectionTest } from "./TableauConnection/useTableauConnectionTest";

const formSchema = z.object({
  url: z
    .string()
    .min(1, "url is required")
    .refine(
      value => !value.startsWith("http://") && !value.startsWith("https://"),
      "Please enter only the url without http:// or https://"
    )
    .refine(value => !value.includes("/"), "Please enter only the url without any slashes")
    .refine(value => {
      // Basic hostname validation
      const hostnameRegex =
        /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      return hostnameRegex.test(value);
    }, "Please enter a valid url"),
  siteName: z.string(),
  patName: z.string().min(1, "Personal Access Token Name is required"),
  patSecret: z.string().min(1, "Personal Access Token Secret is required"),
});

type FormValues = z.infer<typeof formSchema>;

const TableauConnection = ({
  typeData,
  handleBack,
  handleConnectionSubmit,
}: ConnectionProps): JSX.Element => {
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: "",
      siteName: "",
      patName: "",
      patSecret: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    form.setValue("url", (typeData.connection?.host as string) || "");
    form.setValue("siteName", (typeData.connection?.site_name as string) || "");
    form.setValue("patName", (typeData.connection?.pat_name as string) || "");
    form.setValue("patSecret", (typeData.connection?.pat_secret as string) || "");
  }, [typeData.connection, form]);

  const { connectionStatus, isTestingConnection, testDatastoreConnection, canPublishWorkbooks } =
    useTableauConnectionTest();

  const { mutate: createDatastore, isPending: isLoading } = useMutation({
    mutationFn: (data: TableauDatastoreRequest) => addDatastore(data),
    onSuccess: () => {
      navigate("/settings/datastores");
    },
    onError: (err: unknown) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: (err as Error)?.message || "Unable to add Datastore!",
      });
    },
  });

  const getValues = (values: FormValues) => {
    return {
      url: `https://${values.url}`,
      host: values.url,
      site_name: values.siteName || "default",
      pat_name: values.patName,
      pat_secret: values.patSecret,
      user: values.patName,
      password: values.patSecret,
      databases: [values.siteName || "default"],
      ...typeData,
      // Carry can_publish_workbooks captured from the most recent successful
      // 'Test Connection' so Step 3 can adapt its UI for non-publish PATs.
      // Stripped before the backend create call in handleProjectPickerFinish.
      can_publish_workbooks: canPublishWorkbooks ?? undefined,
    };
  };
  const handleTestConnection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    testDatastoreConnection(getValues(form.getValues()));
  };

  const onSubmit = (values: FormValues) => {
    const connectionData = getValues(values);

    // Pass connection data to parent for project picker step
    if (handleConnectionSubmit) {
      handleConnectionSubmit(connectionData);
    } else {
      // Fallback: create datastore directly if no handler provided
      createDatastore(connectionData);
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="al-p-4">
          <TableauConnectionHeader
            connectionStatus={connectionStatus}
            isTestingConnection={isTestingConnection}
          />

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="al-mt-6">
              <Stack gap="4xl" direction="column">
                <FormField
                  control={form.control}
                  name="url"
                  render={({ field }) => (
                    <FormItem>
                      <Label>
                        URL
                        <p className="al-text-xs al-text-gray-500 al-font-light al-mt-1">
                          Enter your Tableau Server hostname (e.g., your.tableau.server)
                        </p>
                      </Label>
                      <FormControl>
                        <InputGroup>
                          <InputGroupAddon align="inline-start">
                            <InputGroupText>https://</InputGroupText>
                          </InputGroupAddon>
                          <InputGroupInput placeholder="your.tableau.server" {...field} />
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="siteName"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Site Name (Optional)</Label>
                      <Typography variant="body" size="xs" className="al-block">
                        Leave blank for default site, or enter your Tableau site name
                      </Typography>
                      <FormControl>
                        <Input
                          placeholder="Leave blank for default or enter site name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="patName"
                  render={({ field }) => (
                    <FormItem>
                      <Label>
                        Personal Access Token (PAT) Name
                        <p className="al-text-xs al-text-gray-500 al-font-light al-mt-1">
                          Create a PAT as <strong>&ldquo;site administrator explorer&rdquo;</strong>{" "}
                          or <strong>&ldquo;site administrator creator&rdquo;</strong> role as per
                          instructions{" "}
                          <a
                            href="https://help.tableau.com/current/server/en-us/security_personal_access_tokens.htm"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="al-text-blue-500 hover:al-text-blue-600"
                          >
                            here
                          </a>{" "}
                          <br />
                          Note: PAT expires if it’s not used for 15 days as per Tableau server
                          policies.
                        </p>
                      </Label>
                      <FormControl>
                        <Input placeholder="altimate_token" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="patSecret"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Personal Access Token (PAT) Secret</Label>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="new-password"
                          placeholder="token_secret_value"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Stack direction="row" justify="between" className="al-items-center">
                  <Button variant={"outline"} asChild>
                    <Link to="/settings/datastores">Cancel</Link>
                  </Button>
                  <Stack direction="row" gap="md">
                    <Button variant={"secondary"} onClick={handleBack}>
                      Back
                    </Button>
                    <LoadingButton
                      variant="default"
                      disabled={!form.formState.isValid}
                      onClick={handleTestConnection}
                      loading={isTestingConnection}
                    >
                      Test Connection
                    </LoadingButton>
                    <LoadingButton
                      variant="default"
                      type="submit"
                      loading={isLoading}
                      className="al-px-4"
                      disabled={!form.formState.isValid || connectionStatus !== "success"}
                    >
                      {handleConnectionSubmit ? "Next" : "Finish"}
                    </LoadingButton>
                  </Stack>
                </Stack>
              </Stack>
            </form>
          </Form>
        </div>
      </CardContent>
    </Card>
  );
};

export default TableauConnection;
